import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { AreaChart, Area, ResponsiveContainer } from "recharts";
import toast from "react-hot-toast";

import axiosConfig from "../../axios/axiosConfig";

import ShowChartIcon from "@mui/icons-material/ShowChart";
import EventIcon from "@mui/icons-material/Event";
import RefreshIcon from "@mui/icons-material/Refresh";
import BarChartIcon from "@mui/icons-material/BarChart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import PaymentsIcon from "@mui/icons-material/Payments";
import CandlestickChartIcon from "@mui/icons-material/CandlestickChart";
import DescriptionIcon from "@mui/icons-material/Description";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// ── palette (brand) ──────────────────────────────────────────────────────
const pageBg = "#eef2f7";
const cardBg = "#ffffff";
const cardBorder = "#e2e8f0";
const navy = "#04343a";
const brandGreen = "#016551";
const negRed = "#dc2626";
const textMain = "#1e293b";
const textMuted = "#64748b";
const cash = "#0284c7";
const futures = "#6d28d9";
const options = "#b45309";

const todayStr = () => new Date().toISOString().slice(0, 10);

const num = (n) => (typeof n === "number" && !isNaN(n) ? n : Number(n) || 0);

const fmtInt = (n) => Math.round(num(n)).toLocaleString("en-IN");
const signColor = (n) => (num(n) < 0 ? negRed : brandGreen);
const fmtPct = (n) => `${num(n) < 0 ? "-" : "+"}${Math.abs(num(n)).toFixed(2)}%`;
const fmtRs = (n) => `${num(n) < 0 ? "-" : "+"}₹${fmtInt(Math.abs(num(n)))}`;

const sumPerf = (rows) => rows.reduce((s, r) => s + num(r.performance), 0);

// cumulative P&L curve, chronological where trade_id is available
const cumulativeSeries = (rows) => {
  const ordered = rows[0]?.trade_id != null
    ? [...rows].sort((a, b) => a.trade_id - b.trade_id)
    : rows;
  let running = 0;
  return ordered.map((r) => (running += num(r.performance)));
};

// ── sub-components ────────────────────────────────────────────────────────
const Sparkline = ({ data, color }) => (
  <ResponsiveContainer width="100%" height={56}>
    <AreaChart data={data.map((v) => ({ v }))} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
      <defs>
        <linearGradient id={`spark-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.35} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2} fill={`url(#spark-${color})`} />
    </AreaChart>
  </ResponsiveContainer>
);

const StatBadge = ({ icon, iconColor, value, label, accent }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, background: "#f8fafc", border: `1px solid ${cardBorder}`, borderRadius: "10px", p: "14px 20px", flex: 1, minWidth: 180 }}>
    <Box sx={{ width: 48, height: 48, borderRadius: "50%", background: iconColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {React.cloneElement(icon, { sx: { color: "#fff", fontSize: 26 } })}
    </Box>
    <Box>
      <Typography sx={{ fontSize: 24, fontWeight: 700, color: textMain, lineHeight: 1.1, fontFamily: "monospace" }}>{value}</Typography>
      <Typography sx={{ fontSize: 12, color: textMuted, mt: "2px" }}>{label}</Typography>
      <Box sx={{ width: 22, height: 3, borderRadius: 2, background: accent, mt: "6px" }} />
    </Box>
  </Box>
);

const MiniStat = ({ label, value, color }) => (
  <Box>
    <Typography sx={{ fontSize: 11, color: textMuted, mb: "4px" }}>{label}</Typography>
    <Typography sx={{ fontSize: 16, fontWeight: 700, color: color || textMain, fontFamily: "monospace" }}>{value}</Typography>
  </Box>
);

const SegmentCard = ({ icon, label, color, tradesClosed, positiveTrades, accuracyPct, children }) => (
  <Box sx={{ flex: 1, minWidth: 300, background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "14px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", p: "18px 20px" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
      <Box sx={{ width: 40, height: 40, borderRadius: "10px", background: color, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {React.cloneElement(icon, { sx: { color: "#fff", fontSize: 22 } })}
      </Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", color }}>{label}</Typography>
    </Box>

    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
      <MiniStat label="Trades Closed" value={tradesClosed} />
      <MiniStat label="Positive Trades" value={positiveTrades} color={brandGreen} />
      <MiniStat label="Accuracy" value={`${num(accuracyPct).toFixed(2)}%`} />
    </Box>

    <Box sx={{ borderTop: `1px dashed ${cardBorder}`, pt: 2 }}>
      {tradesClosed > 0 ? children : (
        <Typography sx={{ fontSize: 12, color: textMuted, textAlign: "center", py: 1 }}>No trades in this period</Typography>
      )}
    </Box>
  </Box>
);

const ProfitBox = ({ label, value, color }) => (
  <Box sx={{ background: `${color}14`, border: `1px solid ${color}40`, borderRadius: "10px", p: "12px 16px", flex: 1 }}>
    <Typography sx={{ fontSize: 11, color: textMuted, mb: "6px" }}>{label}</Typography>
    <Typography sx={{ fontSize: 19, fontWeight: 700, color, fontFamily: "monospace" }}>{value}</Typography>
  </Box>
);

const TopPerformersTable = ({ label, sub, color, rows, unit }) => {
  const [expanded, setExpanded] = useState(false);
  const visibleRows = expanded ? rows : rows.slice(0, 5);
  const fmtVal = unit === "rs" ? fmtRs : fmtPct;

  return (
    <Box sx={{ flex: 1, minWidth: 300, background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "14px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "14px 18px", borderBottom: `1px solid ${cardBorder}` }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color }}>
          {label} <Typography component="span" sx={{ fontSize: 11, color: textMuted, fontWeight: 500 }}>{sub}</Typography>
        </Typography>
        {rows.length > 5 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, cursor: "pointer" }} onClick={() => setExpanded((e) => !e)}>
            <Typography sx={{ fontSize: 12, color: textMuted }}>{expanded ? "Show Less" : "View All"}</Typography>
            <ChevronRightIcon sx={{ fontSize: 16, color: textMuted, transform: expanded ? "rotate(90deg)" : "none" }} />
          </Box>
        )}
      </Box>
      {rows.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: textMuted, textAlign: "center", p: 3 }}>No trades in this period</Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 280 }}>
            <Box component="thead">
              <Box component="tr">
                {["#", "Symbol", unit === "rs" ? "Net Profit (₹)" : "Net Profit (%)"].map((h) => (
                  <Box component="th" key={h} sx={{ textAlign: "left", fontSize: 11, color: textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", background: "#f8fafc", borderBottom: `2px solid ${cardBorder}`, p: "10px 18px", whiteSpace: "nowrap" }}>{h}</Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {visibleRows.map((r, i) => (
                <Box component="tr" key={(r.trade_id ?? r.symbol) + "-" + i} sx={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                  <Box component="td" sx={{ p: "8px 18px", fontSize: 12, color: textMuted, borderBottom: "1px solid #f1f5f9" }}>{i + 1}</Box>
                  <Box component="td" sx={{ p: "8px 18px", fontSize: 12, color: textMain, fontWeight: 600, whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9" }}>{r.symbol}</Box>
                  <Box component="td" sx={{ p: "8px 18px", fontSize: 12, color: signColor(r.performance), fontWeight: 700, fontFamily: "monospace", whiteSpace: "nowrap", borderBottom: "1px solid #f1f5f9" }}>{fmtVal(r.performance)}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ── main page ─────────────────────────────────────────────────────────────
const TradingActivityDashboard = () => {
  const [fromDate, setFromDate] = useState(todayStr());
  const [toDate, setToDate] = useState(todayStr());
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);

  const loadDashboard = async (startDate, endDate) => {
    setLoading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: "support/trades-dashboard/",
        params: { start_date: startDate, end_date: endDate },
      });
      setDashboard(res.data);
    } catch (error) {
      console.log("trades-dashboard error: ", error);
      toast.error(error?.response?.data?.response || "Failed to load trading activity");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard(fromDate, toDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => loadDashboard(fromDate, toDate);
  const handleReset = () => {
    const today = todayStr();
    setFromDate(today);
    setToDate(today);
    loadDashboard(today, today);
  };

  const dateInputSx = {
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.15)",
    color: "#fff",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    fontFamily: "monospace",
    outline: "none",
    colorScheme: "dark",
  };

  const total = dashboard?.total || { trades_closed: 0, positive_trades: 0, accuracy: 0 };
  const cashData = dashboard?.cash || { trades_closed: 0, positive_trades: 0, accuracy: 0 };
  const futData = dashboard?.futures || { trades_closed: 0, positive_trades: 0, accuracy: 0 };
  const optData = dashboard?.option || { trades_closed: 0, positive_trades: 0, accuracy: 0, net_profit_amount: 0 };

  const cashTop = dashboard?.top_performer?.cash || [];
  const futTop = dashboard?.top_performer?.future || [];
  const optTop = dashboard?.top_performer?.option || [];

  const cashNetPct = sumPerf(cashTop);
  const futNetRs = sumPerf(futTop);
  const optNetPct = sumPerf(optTop);

  return (
    <Box sx={{ p: 3, background: pageBg, minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{
        background: navy, borderRadius: "12px",
        boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
        p: "20px 24px", mb: 3,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 2,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: "10px", background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ShowChartIcon sx={{ color: "#1db954", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 20, lineHeight: 1.2 }}>
              Performance Dashboard
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 12, mt: "2px" }}>
              Track your trading performance across all segments
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1.5, flexWrap: "wrap" }}>
          <Box>
            <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 11, mb: "6px" }}>Date Filter</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EventIcon sx={{ color: "rgba(255,255,255,0.5)", fontSize: 18 }} />
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={dateInputSx} />
              <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>to</Typography>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={dateInputSx} />
            </Box>
          </Box>
          <Box
            component="button"
            onClick={handleApply}
            sx={{
              background: brandGreen, color: "#fff", border: "none", borderRadius: "8px",
              padding: "9px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer",
              boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)", boxShadow: "0 6px 16px rgba(1,101,81,0.4)" },
            }}
          >
            Apply
          </Box>
          <Box
            component="button"
            onClick={handleReset}
            sx={{
              display: "flex", alignItems: "center", gap: 0.75,
              background: "rgba(255,255,255,0.08)", color: "#fff", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "8px",
              padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer",
              "&:hover": { background: "rgba(255,255,255,0.14)" },
            }}
          >
            <RefreshIcon sx={{ fontSize: 16 }} /> Reset Filter
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: brandGreen }} />
        </Box>
      ) : (
        <>
          {/* TOTAL */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 3, background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "12px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", p: "18px 20px", mb: 3, flexWrap: "wrap" }}>
            <Box sx={{ minWidth: 140 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: navy, letterSpacing: "0.06em" }}>TOTAL</Typography>
              <Typography sx={{ fontSize: 12, color: textMuted, mt: "2px" }}>Overall Performance</Typography>
            </Box>
            <StatBadge icon={<BarChartIcon />} iconColor={cash} value={fmtInt(total.trades_closed)} label="Trades Closed" accent={cash} />
            <StatBadge icon={<CheckCircleIcon />} iconColor={brandGreen} value={fmtInt(total.positive_trades)} label="Positive Trades" accent={brandGreen} />
            <StatBadge icon={<GpsFixedIcon />} iconColor={futures} value={`${num(total.accuracy).toFixed(2)}%`} label="Accuracy" accent={futures} />
          </Box>

          {/* Segment cards */}
          <Box sx={{ display: "flex", gap: 2.5, mb: 3, flexWrap: "wrap" }}>
            <SegmentCard icon={<PaymentsIcon />} label="CASH" color={cash} tradesClosed={cashData.trades_closed} positiveTrades={cashData.positive_trades} accuracyPct={cashData.accuracy}>
              <Typography sx={{ fontSize: 11, color: textMuted, mb: "4px" }}>Net Profit (in %)</Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: signColor(cashNetPct), mb: 1, fontFamily: "monospace" }}>{fmtPct(cashNetPct)}</Typography>
              {cashTop.length > 1 && <Sparkline data={cumulativeSeries(cashTop)} color={cash} />}
            </SegmentCard>

            <SegmentCard icon={<CandlestickChartIcon />} label="FUTURES" color={futures} tradesClosed={futData.trades_closed} positiveTrades={futData.positive_trades} accuracyPct={futData.accuracy}>
              <Typography sx={{ fontSize: 11, color: textMuted, mb: "4px" }}>Net Profit (in ₹)</Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: signColor(futNetRs), mb: 1, fontFamily: "monospace" }}>{fmtRs(futNetRs)}</Typography>
              {futTop.length > 1 && <Sparkline data={cumulativeSeries(futTop)} color={futures} />}
            </SegmentCard>

            <SegmentCard icon={<DescriptionIcon />} label="OPTIONS" color={options} tradesClosed={optData.trades_closed} positiveTrades={optData.positive_trades} accuracyPct={optData.accuracy}>
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <ProfitBox label="Net Profit (in ₹)" value={fmtRs(optData.net_profit_amount)} color={signColor(optData.net_profit_amount)} />
                <ProfitBox label="Net Profit (in %)" value={fmtPct(optNetPct)} color={signColor(optNetPct)} />
              </Box>
            </SegmentCard>
          </Box>

          {/* Top performers */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
            <EmojiEventsIcon sx={{ color: "#f5a623", fontSize: 20 }} />
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: navy }}>Top Performers</Typography>
              <Typography sx={{ fontSize: 11, color: textMuted }}>Best performing trades in each segment</Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2.5, mb: 2, flexWrap: "wrap" }}>
            <TopPerformersTable label="CASH" sub="(Top by %)" color={cash} rows={cashTop} unit="pct" />
            <TopPerformersTable label="FUTURES" sub="(Top by ₹)" color={futures} rows={futTop} unit="rs" />
            <TopPerformersTable label="OPTIONS" sub="(Top by %)" color={options} rows={optTop} unit="pct" />
          </Box>

          <Typography sx={{ textAlign: "center", fontSize: 12, color: textMuted, mt: 1 }}>
            Note: Accuracy = (Positive Trades / Total Trades Closed) × 100
          </Typography>
        </>
      )}
    </Box>
  );
};

export default TradingActivityDashboard;
